<script setup>
/* global axios */
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';

const props = defineProps({
  messageId: { type: Number, required: true },
  conversationId: { type: Number, required: true },
});
const { accountId } = useAccount();
const { t } = useI18n();
const state = ref(null);
const error = ref(false);
const busy = ref(false);
const expanded = ref(false);
const notes = ref({});
const receipts = ref({});
const actionRejected = ref(false);
let generation = 0;
let timer;
let controller;
const base = () => `/api/v1/accounts/${accountId.value}/lilith/conversations/${props.conversationId}/messages/${props.messageId}/delivery`;
const label = computed(() => {
  if (error.value) return 'UNAVAILABLE';
  const counts = state.value?.counts || {};
  if (counts.UNKNOWN) return 'UNKNOWN';
  if (counts.FAILED) return 'FAILED';
  if (state.value?.active) return 'PENDING';
  if (counts.RECONCILED) return 'RECONCILED';
  if (state.value?.transport_status === 'ACCEPTED') return 'ACCEPTED';
  if (state.value?.transport_status === 'QUEUED') return 'QUEUED';
  return '';
});

async function load(action = '', body = {}) {
  if (busy.value) return;
  clearTimeout(timer);
  const current = generation;
  busy.value = true;
  controller = new AbortController();
  try {
    const options = { signal: controller.signal };
    const response = action
      ? await axios.post(`${base()}/${action}`, body, options)
      : await axios.get(base(), options);
    if (current !== generation) return;
    state.value = response.data;
    error.value = false;
    actionRejected.value = false;
    if (response.data.active) timer = setTimeout(() => load(), 5000);
  } catch (failure) {
    if (current !== generation || failure.code === 'ERR_CANCELED') return;
    if (action && [409, 422].includes(failure.response?.status)) {
      actionRejected.value = true;
      return;
    }
    error.value = true;
    if ([401, 403, 404].includes(failure.response?.status)) state.value = null;
  } finally {
    if (current === generation) busy.value = false;
  }
}

watch([accountId, () => props.messageId, () => props.conversationId], () => {
  generation += 1;
  controller?.abort();
  clearTimeout(timer);
  state.value = null;
  notes.value = {};
  receipts.value = {};
  actionRejected.value = false;
  expanded.value = false;
  busy.value = false;
  load();
}, { immediate: true });
onBeforeUnmount(() => { generation += 1; controller?.abort(); clearTimeout(timer); });
</script>

<template>
  <div v-if="label" class="skip-context-menu flex flex-col gap-1 max-w-sm text-xs text-n-slate-11">
    <button type="button" class="text-left underline" @click.stop="expanded = !expanded">
      {{ t(`TEAM_LILITH.DELIVERY.${label}`) }}
    </button>
    <div v-if="expanded" class="flex flex-col gap-2 p-2 rounded border border-n-weak">
      <p v-if="label === 'UNKNOWN'">{{ t('TEAM_LILITH.DELIVERY.UNKNOWN_HELP') }}</p>
      <p v-if="actionRejected" role="alert">{{ t('TEAM_LILITH.DELIVERY.ACTION_REJECTED') }}</p>
      <button type="button" :disabled="busy" @click.stop="load()">{{ t('TEAM_LILITH.DELIVERY.REFRESH') }}</button>
      <div v-for="operation in state?.operations || []" :key="operation.id" class="flex flex-col gap-1">
        <span>{{ t(`TEAM_LILITH.DELIVERY.KINDS.${operation.kind}`) }} — {{ t(`TEAM_LILITH.DELIVERY.${operation.status}`) }}</span>
        <span v-if="operation.evidence">{{ operation.evidence }}</span>
        <span v-if="operation.late_provider_state">{{ t('TEAM_LILITH.DELIVERY.LATE_RESULT', { state: t(`TEAM_LILITH.DELIVERY.${operation.late_provider_state}`) }) }}</span>
        <button v-if="state.can_manage && operation.retryable" type="button" :disabled="busy"
                @click.stop="load(`${operation.id}/retry`)">{{ t('TEAM_LILITH.DELIVERY.RETRY') }}</button>
        <template v-if="state.can_manage && operation.status === 'UNKNOWN'">
          <textarea v-model="notes[operation.id]" maxlength="2000" :aria-label="t('TEAM_LILITH.DELIVERY.EVIDENCE')"
                    :placeholder="t('TEAM_LILITH.DELIVERY.EVIDENCE')" class="rounded border border-n-weak bg-n-solid-1 p-1" />
          <input v-if="operation.kind === 'PART'" v-model="receipts[operation.id]" maxlength="2000"
                 :aria-label="t('TEAM_LILITH.DELIVERY.PROVIDER_IDS')" :placeholder="t('TEAM_LILITH.DELIVERY.PROVIDER_IDS')"
                 class="rounded border border-n-weak bg-n-solid-1 p-1" />
          <button type="button" :disabled="busy || !notes[operation.id]?.trim()"
                  @click.stop="load(`${operation.id}/reconcile`, { decision: 'confirmed', evidence: notes[operation.id], provider_ids: receipts[operation.id] })">
            {{ t('TEAM_LILITH.DELIVERY.CONFIRMED') }}
          </button>
          <button type="button" :disabled="busy || !notes[operation.id]?.trim()"
                  @click.stop="load(`${operation.id}/reconcile`, { decision: 'abandoned', evidence: notes[operation.id] })">
            {{ t('TEAM_LILITH.DELIVERY.ABANDONED') }}
          </button>
        </template>
      </div>
    </div>
  </div>
</template>
